/**
 * MoveGoodsPhase - 商品輸送フェーズUIコンポーネント
 *
 * FRONTEND.md Step.3 準拠:
 * - Lovable スタイル適用済み
 *
 * @see FRONTEND.md
 * @see src/types/ui-interfaces.ts
 */

import type { MoveGoodsPhaseProps } from '../../types/ui-interfaces';
import { useTranslations, interpolate } from '../../i18n';

/**
 * Phase 5: 商品輸送フェーズUI（共通）
 *
 * 機能:
 * - 輸送可能な商品リスト表示
 * - 最適経路の表示
 * - 代替経路の選択（Request 9: 手動経路選択）
 * - 機関車強化オプション
 * - 輸送確定とパス
 */
export function MoveGoodsPhase({
  player,
  transportableGoods,
  selectedGoods,
  bestRoute,
  isMyTurn,
  showUpgradeConfirm,
  onSelectGoods,
  onConfirmTransport,
  onUpgradeLoco,
  onShowUpgradeConfirm,
  onHideUpgradeConfirm,
  onPass,
}: MoveGoodsPhaseProps) {
  const t = useTranslations();
  // 機関車強化可能かどうか
  const canUpgrade = !player.hasUpgradedLocoThisTurn && player.locomotiveLevel < 6;

  return (
    <div className="flex flex-col h-full">
      {!isMyTurn ? (
        <p className="text-muted-foreground text-sm">{t.common.waiting}</p>
      ) : (
        <>
          {/* コンテンツエリア */}
          <div className="flex-1 min-h-0 flex flex-col space-y-2 pb-2">
            {/* 経路切り替えのヒント */}
            <div className="flex-shrink-0 text-xs text-muted-foreground">
              {t.moveGoods.guide}
            </div>

            {/* 輸送可能な商品リスト - 残りスペースを使用 */}
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex-shrink-0 text-xs font-medium mb-1">
                {interpolate(t.moveGoods.transportable, { count: transportableGoods.length })}
              </div>
              {transportableGoods.length === 0 ? (
                <div className="text-muted-foreground text-xs">{t.moveGoods.noGoodsAvailable}</div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 gap-1.5">
                    {transportableGoods.map((goods, i) => {
                      const isSelected = selectedGoods?.cityId === goods.cityId && selectedGoods?.color === goods.color;
                      const hasMultipleRoutes = goods.availableLinkCounts.length > 1;
                      return (
                        <button
                          key={`${goods.cityId}-${goods.color}-${i}`}
                          className="goods-card text-left text-xs"
                          onClick={() => onSelectGoods(goods)}
                          data-selected={isSelected}
                          data-goods-color={goods.color}
                        >
                          <div className="flex justify-between items-start">
                            <div className="font-medium">{goods.displayLabel}</div>
                            {isSelected && hasMultipleRoutes && (
                              <span className="text-primary text-sm">⟳</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className={`w-4 h-4 rounded-sm bg-goods-${goods.color}`} />
                            <span className="text-[10px] text-muted-foreground">
                              Links: {goods.availableLinkCounts.join(', ')}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* 機関車強化確認ダイアログ */}
            {showUpgradeConfirm && (
              <div className="flex-shrink-0 message-info p-2 space-y-1.5">
                <div className="text-xs font-medium">{t.moveGoods.confirmUpgrade}</div>
                <div className="flex gap-2">
                  <button className="btn-primary flex-1 text-xs py-1" onClick={onUpgradeLoco}>
                    {t.common.confirm}
                  </button>
                  <button className="btn-ghost flex-1 text-xs py-1" onClick={onHideUpgradeConfirm}>
                    {t.common.cancel}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 固定アクションボタン（横並び・横幅いっぱいに配分） */}
          <div className="flex-shrink-0 flex gap-2 pt-2 border-t border-border bg-card">
            <button
              className="btn-primary flex-1 text-xs py-2 whitespace-nowrap"
              onClick={onConfirmTransport}
              disabled={!selectedGoods || !bestRoute}
            >
              {t.common.confirm}
            </button>
            <button
              className="btn-secondary flex-1 text-xs py-2 whitespace-nowrap"
              onClick={onShowUpgradeConfirm}
              disabled={!canUpgrade}
            >
              {player.locomotiveLevel >= 6 ? t.moveGoods.maxLocoLevel : t.moveGoods.upgradeLoco}
            </button>
            <button
              className="btn-ghost flex-1 text-xs py-2 whitespace-nowrap"
              onClick={onPass}
            >
              {t.common.pass}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default MoveGoodsPhase;
